import { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { isToday, isYesterday, format } from 'date-fns';
import { Check, CheckCheck, X, MessageSquare, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { OperatorMessage } from '@/lib/operator-api/types';

/** Returns a label only for human operator messages */
function getSenderLabel(message: OperatorMessage): string | null {
  if (message.direction === 'INCOMING') return null;
  if (message.type === 'OPERATOR' || message.type === 'operator') return 'Operator';
  return null;
}

function getMessageText(message: OperatorMessage): string {
  if (message.text) return message.text;
  if (typeof message.content === 'string') return message.content;
  if (message.content && typeof message.content === 'object' && 'text' in message.content) {
    return message.content.text;
  }
  return '';
}

function isMasked(value: unknown): boolean {
  if (!value || typeof value !== 'string') return false;
  return value.includes('***') || value.includes('*');
}

function formatTime(dateString: string): string {
  return new Date(dateString).toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getDateLabel(dateStr: string, t: (key: string) => string): string {
  const d = new Date(dateStr);
  if (isToday(d)) return t('conversations.chat.today');
  if (isYesterday(d)) return t('conversations.chat.yesterday');
  return format(d, 'MMM d, yyyy');
}

interface MessageListProps {
  messages: OperatorMessage[];
  canViewFullPII?: boolean;
}

export function MessageList({ messages, canViewFullPII }: MessageListProps) {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const [showScrollFab, setShowScrollFab] = useState(false);
  const prevCountRef = useRef(messages.length);

  const scrollToBottom = useCallback((smooth = true) => {
    bottomRef.current?.scrollIntoView({ behavior: smooth ? 'smooth' : 'instant' });
  }, []);

  // Auto-scroll on new messages
  useEffect(() => {
    if (messages.length > prevCountRef.current) {
      scrollToBottom();
    }
    prevCountRef.current = messages.length;
  }, [messages.length, scrollToBottom]);

  // Initial scroll to bottom
  useEffect(() => {
    scrollToBottom(false);
  }, [scrollToBottom]);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const isNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 100;
    setShowScrollFab(!isNearBottom);
  }, []);

  if (messages.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground">
        <MessageSquare className="h-10 w-10 mb-3 opacity-40" />
        <p className="font-medium">{t('conversations.chat.noMessages')}</p>
        {!canViewFullPII && (
          <p className="text-sm mt-1">{t('conversations.chat.piiRedacted')}</p>
        )}
      </div>
    );
  }

  // Group messages by day for date separators
  let lastDate: string | null = null;

  return (
    <div
      ref={scrollRef}
      className="flex-1 overflow-y-auto px-4 py-3 relative"
      style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'400\' height=\'400\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cdefs%3E%3Cpattern id=\'p\' width=\'20\' height=\'20\' patternUnits=\'userSpaceOnUse\'%3E%3Ccircle cx=\'1\' cy=\'1\' r=\'.6\' fill=\'%23e5e7eb\' fill-opacity=\'.3\'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width=\'400\' height=\'400\' fill=\'url(%23p)\'/%3E%3C/svg%3E")' }}
      onScroll={handleScroll}
    >
      <div className="max-w-2xl mx-auto space-y-1">
        {messages.map((message) => {
          const msgDate = new Date(message.timestamp);
          const dateKey = format(msgDate, 'yyyy-MM-dd');
          let showDateSep = false;
          if (dateKey !== lastDate) {
            showDateSep = true;
            lastDate = dateKey;
          }

          const isIncoming = message.direction === 'INCOMING';
          const text = getMessageText(message);
          const masked = isMasked(text);
          const senderLabel = getSenderLabel(message);

          return (
            <div key={message.id}>
              {showDateSep && (
                <div className="flex items-center justify-center my-3">
                  <span className="bg-white/90 dark:bg-muted/90 text-muted-foreground text-[11px] px-3 py-1 rounded-full shadow-sm">
                    {getDateLabel(message.timestamp, t)}
                  </span>
                </div>
              )}
              <div className={cn('flex mb-1', isIncoming ? 'justify-start' : 'justify-end')}>
                <div
                  className={cn(
                    'max-w-[75%] rounded-lg px-3 py-1.5 shadow-sm',
                    isIncoming
                      ? 'bg-white dark:bg-muted rounded-bl-sm'
                      : 'bg-[#d9fdd3] dark:bg-emerald-900/40 rounded-br-sm'
                  )}
                >
                  {senderLabel && (
                    <span className="text-[11px] font-medium text-emerald-700 mb-0.5 block">
                      {senderLabel}
                    </span>
                  )}
                  <p
                    className={cn(
                      'text-sm leading-relaxed whitespace-pre-wrap break-words',
                      masked && 'text-amber-700 bg-amber-50 px-1 rounded'
                    )}
                  >
                    {text}
                    <span className="inline-flex items-center float-right ml-2 mt-1 gap-0.5">
                      <span className="text-[10px] text-muted-foreground">
                        {formatTime(message.timestamp)}
                      </span>
                      {message.status && message.direction === 'OUTGOING' && (
                        <>
                          {message.status === 'SENT' && <Check className="h-3 w-3 text-muted-foreground" />}
                          {message.status === 'DELIVERED' && <CheckCheck className="h-3 w-3 text-muted-foreground" />}
                          {message.status === 'READ' && <CheckCheck className="h-3 w-3 text-blue-500" />}
                          {message.status === 'FAILED' && <X className="h-3 w-3 text-red-500" />}
                        </>
                      )}
                    </span>
                  </p>
                </div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Scroll to bottom FAB */}
      {showScrollFab && (
        <button
          type="button"
          onClick={() => scrollToBottom()}
          className="absolute bottom-4 right-4 bg-background border shadow-lg rounded-full p-2 hover:bg-muted transition-colors z-10"
        >
          <ChevronDown className="h-5 w-5 text-muted-foreground" />
        </button>
      )}
    </div>
  );
}
